import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader } from 'lucide-react';
import {
  getPageAccessConfig,
  canAccessPage,
  getUserRolesFromToken,
  getActiveRestaurantId,
  getActiveRole,
} from '../utils/roleAccess';

const PATH_TO_PAGE_KEY: Record<string, string> = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/place-order': 'place-order',
  '/orders': 'orders',
  '/tables': 'tables',
  '/customers': 'customers',
  '/items': 'items',
  '/kitchens': 'kitchens',
  '/reports': 'reports',
  '/profile': 'profile',
  '/settings': 'settings',
  '/organization-onboarding': 'organization-onboarding',
  '/employee-management': 'employee-management',
  '/role-page-config': 'role-page-config',
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Compute effective roles (same logic as Layout)
  const activeRestaurantId = getActiveRestaurantId();
  const activeRole = getActiveRole();
  let effectiveRoles: string[];
  if (user.restaurantRoles?.length && activeRestaurantId) {
    const entry = user.restaurantRoles.find(rr => rr.restaurantId === activeRestaurantId);
    const restaurantRoles = entry ? (entry.roles as string[]) : getUserRolesFromToken();
    effectiveRoles = (activeRole && restaurantRoles.includes(activeRole))
      ? [activeRole]
      : restaurantRoles;
  } else {
    effectiveRoles = getUserRolesFromToken();
  }

  // Check if the current role has access to this page
  const pageKey = PATH_TO_PAGE_KEY[location.pathname];
  if (pageKey) {
    const config = getPageAccessConfig();
    if (!canAccessPage(pageKey, effectiveRoles, config)) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};